import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { Badge } from "../Badge";
import { formatDate, formatTime } from "./CaregiverDashboard";

function startOfWeek(offset) {
  const d = new Date();
  d.setDate(d.getDate() - d.getDay() + offset * 7);
  return d;
}

function fmt(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function MySchedule() {
  const [week, setWeek] = useState(0);
  const [visits, setVisits] = useState(null);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = startOfWeek(week);
    d.setDate(d.getDate() + i);
    return d;
  }), [week]);

  useEffect(() => {
    setVisits(null);
    api.get(`/caregiver/visits?date_from=${fmt(days[0])}&date_to=${fmt(days[6])}`)
      .then((r) => setVisits(r.data))
      .catch(() => setVisits([]));
  }, [week, days]);

  return (
    <div data-testid="caregiver-schedule">
      <h1 className="font-display text-3xl md:text-4xl text-brand-navy">My Schedule</h1>
      <p className="mt-2 text-[15px] text-slate-600">Your assigned visits for the selected week.</p>

      <div className="mt-6 flex gap-2">
        <button onClick={() => setWeek(0)} data-testid="this-week-tab" className={`rounded-full px-6 py-2.5 text-sm font-bold transition-colors duration-300 ${week === 0 ? "bg-brand-navy text-white" : "bg-white border border-slate-300 text-brand-navy"}`}>This Week</button>
        <button onClick={() => setWeek(1)} data-testid="next-week-tab" className={`rounded-full px-6 py-2.5 text-sm font-bold transition-colors duration-300 ${week === 1 ? "bg-brand-navy text-white" : "bg-white border border-slate-300 text-brand-navy"}`}>Next Week</button>
      </div>

      {visits === null ? (
        <p className="mt-8 text-slate-500 font-semibold">Loading schedule…</p>
      ) : (
        <div className="mt-6 space-y-4">
          {days.map((d) => {
            const key = fmt(d);
            const dayVisits = visits.filter((v) => v.date === key);
            return (
              <section key={key} data-testid={`schedule-day-${key}`} className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                <header className="px-5 py-3 bg-brand-mist/60 border-b border-slate-100">
                  <p className="font-bold text-brand-navy text-[15px]">{formatDate(key)}</p>
                </header>
                <div className="p-4 space-y-2">
                  {dayVisits.length === 0 && <p className="text-sm text-slate-400 px-1">No visits scheduled.</p>}
                  {dayVisits.map((v) => (
                    <Link key={v.id} to={`/caregiver/visits/${v.id}`} data-testid={`schedule-visit-${v.id}`} className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 px-4 py-3 hover:border-brand-teal/40 transition-colors duration-300">
                      <div className="min-w-0">
                        <p className="font-bold text-brand-navy truncate">{v.patient_name}</p>
                        <p className="text-sm text-slate-500">{formatTime(v.start_time)} – {formatTime(v.end_time)} · {v.care_type}</p>
                      </div>
                      <Badge kind="visit" value={v.status} />
                    </Link>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
